import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=loading, false=anon, obj=auth
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Tell the server this person's timezone so reminder emails show times in their own zone.
  useEffect(() => {
    if (!user?.id) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && tz !== user.timezone) api.post("/auth/timezone", { timezone: tz }).catch(() => {});
  }, [user?.id, user?.timezone]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.token) localStorage.setItem("cc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("cc_token");
    setUser(false);
  };

  const impersonate = async (userId) => {
    const { data } = await api.post(`/admin/impersonate/${userId}`);
    if (data.token) localStorage.setItem("cc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  const stopImpersonate = async () => {
    const { data } = await api.post("/admin/stop-impersonate");
    if (data.token) localStorage.setItem("cc_token", data.token);
    setUser(data.user);
    return data.user;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setUser, impersonate, stopImpersonate }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
